import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Logo from '@/components/Logo';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { bg: '#03050A', card: '#0B1120', border: 'rgba(255,255,255,0.07)', white: '#F2F6FA', muted: '#8899AA', fog: '#445566', teal: '#00D4C8', gold: '#C9A84C', violet: '#7C5CFC', green: '#2EC8A0', red: '#FF3B5C' };

const PIPELINE = ['Market Map', 'Longlist', 'Shortlist', 'Gate 1', 'Engage', 'Gate 2', 'On Stage'];
const STAGE_MAP = { market_map: 0, longlist: 1, shortlist: 2, engage: 4, on_stage: 6 };

const CandidatePortal = () => {
  const { searchId, candidateId } = useParams();
  const [verified, setVerified] = useState(false);
  const [portalData, setPortalData] = useState(null);
  const [pin, setPin] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [answers, setAnswers] = useState(['', '', '']);
  const [cvFile, setCvFile] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const isPreview = new URLSearchParams(window.location.search).get('preview') === 'true';

  const handlePinChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...pin];
    next[idx] = val;
    setPin(next);
    setPinError('');
    if (val && idx < 3) inputRefs[idx + 1].current?.focus();
    if (val && idx === 3) verifyPin(next.join(''));
  };

  const verifyPin = async (code) => {
    try {
      const res = await axios.post(`${API}/candidate/verify/${searchId}/${candidateId}`, { pin: code });
      setPortalData(res.data);
      setVerified(true);
      if (res.data.candidate?.submission_submitted_at) setSubmitted(true);
      if (res.data.candidate?.submission) {
        const sub = res.data.candidate.submission;
        if (Array.isArray(sub)) setAnswers(sub);
      }
    } catch (err) {
      const left = 3 - attempts - 1;
      setAttempts((p) => p + 1);
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
      setPinError(left > 0 ? `Incorrect PIN (${left} attempt${left !== 1 ? 's' : ''} remaining)` : 'Please contact your search lead for a new PIN');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidate/submit/${candidateId}`, { answer_1: answers[0], answer_2: answers[1], answer_3: answers[2], cv_filename: cvFile });
      setSubmitted(true);
      toast.success('Submission received');
    } catch { toast.error('Submission failed'); }
    finally { setSubmitting(false); }
  };

  // PIN Entry screen
  if (!verified) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
          <Logo size="nav" />
          <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 18, color: 'rgba(242,246,250,0.8)', marginTop: 40, lineHeight: 1.6 }}>
            You've been invited to explore an exclusive opportunity
          </p>
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginTop: 24, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Enter your 4-digit access PIN
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
            {pin.map((d, i) => (
              <input key={i} ref={inputRefs[i]} value={d} onChange={(e) => handlePinChange(i, e.target.value)} maxLength={1}
                style={{ width: 48, height: 56, textAlign: 'center', fontSize: 24, fontFamily: "'Bebas Neue', sans-serif", background: C.card, border: `1px solid ${pinError ? C.red : C.border}`, borderRadius: 8, color: C.white, outline: 'none', caretColor: C.teal }}
                onKeyDown={(e) => { if (e.key === 'Backspace' && !d && i > 0) inputRefs[i - 1].current?.focus(); }}
                disabled={attempts >= 3} />
            ))}
          </div>
          {pinError && <p style={{ fontSize: 13, color: C.red, marginBottom: 12 }}>{pinError}</p>}
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.fog, marginTop: 32 }}>Confidential . Powered by reAImagination . Shim Partners</p>
        </div>
      </div>
    );
  }

  // Portal screen
  const { candidate, search } = portalData || {};
  const brief = search?.brief;
  const stageIdx = STAGE_MAP[search?.status] || 0;
  const TABS = ['Opportunity', 'Journey', 'Submission', 'Offer', 'My Data'];
  const canSubmit = answers[0].length > 10 && answers[1].length > 10 && cvFile;
  const questions = [
    'What draws you to this type of engagement over a permanent role?',
    'Describe a transformation you led that is most relevant to this opportunity.',
    'What would be your first 90-day priority in this role?',
  ];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif", color: C.white }}>
      {/* Preview banner */}
      {isPreview && (
        <div style={{ background: `${C.gold}20`, borderBottom: `1px solid ${C.gold}40`, padding: '8px 24px', textAlign: 'center', fontSize: 12, color: C.gold }}>
          Preview Mode . You are viewing as the candidate
        </div>
      )}

      {/* Top bar */}
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Logo size="small" />
        <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.fog, textTransform: 'uppercase' }}>Confidential</span>
      </div>

      {/* Search lead card */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 24px 0' }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px', marginBottom: 24 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: C.white }}>Debabrat Mishra</p>
          <p style={{ fontSize: 12, color: C.muted }}>Managing Partner, Shim Partners</p>
          {candidate?.portal_message && <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 13, color: 'rgba(242,246,250,0.7)', marginTop: 8, lineHeight: 1.6 }}>"{candidate.portal_message}"</p>}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setActiveTab(i)} style={{
              padding: '10px 16px', fontSize: 12, fontWeight: activeTab === i ? 600 : 400,
              color: activeTab === i ? C.teal : C.muted, background: 'none', border: 'none',
              borderBottom: activeTab === i ? `2px solid ${C.teal}` : '2px solid transparent',
              cursor: 'pointer', transition: 'all 0.2s',
            }}>{t}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ paddingBottom: 80 }}>

          {/* TAB 0: Opportunity */}
          {activeTab === 0 && (
            <div>
              <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: C.teal, marginBottom: 8 }}>{search?.role_title}</h1>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>{search?.role_function}</p>
              <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, padding: '4px 10px', borderRadius: 4, background: `${C.gold}18`, color: C.gold }}>
                {(search?.workforce_type || '').replace('borrow_', '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
              {brief ? (
                <div style={{ marginTop: 24 }}>
                  <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 15, color: 'rgba(242,246,250,0.85)', lineHeight: 1.8, marginBottom: 20 }}>{brief.role_summary}</p>
                  {brief.transformation_context && (<><p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 6 }}>Why now</p><p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 14, color: 'rgba(242,246,250,0.7)', lineHeight: 1.7, marginBottom: 20 }}>{brief.transformation_context}</p></>)}
                  {brief.key_responsibilities?.length > 0 && (<><p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 6 }}>What you'll own</p><ul style={{ margin: '0 0 20px', padding: 0, listStyle: 'none' }}>{brief.key_responsibilities.map((r, i) => <li key={i} style={{ fontFamily: "'Instrument Serif', serif", fontSize: 14, color: 'rgba(242,246,250,0.7)', lineHeight: 1.7, paddingLeft: 16, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: C.teal }}>{'\u2192'}</span>{r}</li>)}</ul></>)}
                  {brief.ideal_profile && (<><p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 6 }}>What we're looking for</p><p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 14, color: 'rgba(242,246,250,0.7)', lineHeight: 1.7, marginBottom: 20 }}>{brief.ideal_profile}</p></>)}
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: C.muted }}>{brief.engagement_type && <span>{brief.engagement_type}</span>}{brief.timeline && <span>{brief.timeline}</span>}</div>
                </div>
              ) : (
                <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 14, color: C.fog, marginTop: 24 }}>Role details being finalised. Check back soon.</p>
              )}
            </div>
          )}

          {/* TAB 1: Journey */}
          {activeTab === 1 && (
            <div>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 16 }}>Your progress</p>
              {PIPELINE.map((stage, i) => {
                const done = i < stageIdx;
                const current = i === stageIdx;
                return (
                  <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${current ? C.teal : done ? C.green : C.fog}`, background: done ? C.green : current ? `${C.teal}20` : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: done ? C.bg : current ? C.teal : C.fog, fontWeight: 700 }}>
                      {done ? '\u2713' : ''}
                    </div>
                    <span style={{ fontSize: 13, color: current ? C.teal : done ? C.white : C.fog, fontWeight: current ? 600 : 400 }}>{stage}</span>
                    {current && <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.teal, padding: '2px 6px', background: `${C.teal}15`, borderRadius: 4 }}>Current</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 2: Submission */}
          {activeTab === 2 && (
            <div>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${C.green}20`, border: `2px solid ${C.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 20, color: C.green }}>{'\u2713'}</div>
                  <p style={{ fontSize: 16, fontWeight: 600, color: C.white, marginBottom: 8 }}>Your submission has been received</p>
                  <p style={{ fontSize: 13, color: C.muted }}>We will be in touch within 48 hours.</p>
                </div>
              ) : (
                <div>
                  {questions.map((q, i) => (
                    <div key={i} style={{ marginBottom: 20 }}>
                      <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 14, color: C.white, marginBottom: 8, lineHeight: 1.6 }}>{q}</p>
                      <textarea value={answers[i]} onChange={(e) => { const n = [...answers]; n[i] = e.target.value.slice(0, 500); setAnswers(n); }} maxLength={500} placeholder="Your answer..." style={{ width: '100%', minHeight: 80, padding: '10px 12px', fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.white, outline: 'none', resize: 'vertical', fontFamily: "'Inter', sans-serif" }} />
                      <span style={{ fontSize: 10, color: C.fog, float: 'right' }}>{answers[i].length}/500</span>
                    </div>
                  ))}
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 13, color: C.white, marginBottom: 8 }}>Upload your CV (PDF)</p>
                    <input type="file" accept=".pdf" onChange={(e) => setCvFile(e.target.files?.[0]?.name || '')} style={{ fontSize: 12, color: C.muted }} />
                    {cvFile && <p style={{ fontSize: 11, color: C.teal, marginTop: 4 }}>{cvFile}</p>}
                  </div>
                  <button onClick={handleSubmit} disabled={!canSubmit || submitting} style={{ padding: '14px 32px', fontSize: 15, fontWeight: 600, background: canSubmit ? C.gold : C.fog, color: canSubmit ? C.bg : C.muted, border: 'none', borderRadius: 10, cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: submitting ? 0.6 : 1, width: '100%' }}>
                    {submitting ? 'Submitting...' : 'Submit Application \u2192'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Offer */}
          {activeTab === 3 && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: 32, color: C.fog, marginBottom: 16 }}>{'\uD83D\uDD12'}</div>
              <p style={{ fontSize: 15, color: C.fog }}>Your offer will appear here once it has been prepared</p>
            </div>
          )}

          {/* TAB 4: My Data */}
          {activeTab === 4 && (
            <div>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 12 }}>Information we hold about you</p>
              {[candidate?.name && 'Name', candidate?.current_title && 'Current role', candidate?.location && 'Location', 'Contact details', cvFile && 'CV uploaded', submitted && 'Submission responses'].filter(Boolean).map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: C.white }}>
                  <span style={{ color: C.green }}>{'\u2713'}</span> {f}
                </div>
              ))}
              <div style={{ marginTop: 32, padding: '16px 0', borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: C.white, marginBottom: 8 }}>Talent Network</p>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Receive notifications about relevant transformation opportunities.</p>
                <button onClick={() => toast.success('Preferences saved')} style={{ padding: '8px 16px', fontSize: 12, background: C.teal, color: C.bg, border: 'none', borderRadius: 6, cursor: 'pointer' }}>Join Talent Network</button>
              </div>
              <div style={{ marginTop: 24 }}>
                <button onClick={() => { if (window.confirm('Request deletion of all data?')) toast.info('Deletion requested. Data will be removed within 48 hours.'); }} style={{ background: 'none', border: 'none', color: C.red, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                  Request data deletion
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidatePortal;
